import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { subscriptionsApi } from '@/lib/api'
import { useNotificationStore } from './notifications'
import type { SubscriptionDetails, CanAddUserResponse, PrepareCheckoutResponse, AiUsage, AiAddonPreview } from '@/types'

export const useSubscriptionsStore = defineStore('subscriptions', () => {
  const subscription = ref<SubscriptionDetails | null>(null)
  const aiUsage = ref<AiUsage | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  const notifications = useNotificationStore()

  // Computed properties
  const isSubscriptionActive = computed(() =>
    subscription.value?.status === 'active'
  )

  const isSubscriptionFree = computed(() =>
    subscription.value?.status === 'free'
  )

  const isSubscriptionPastDue = computed(() =>
    subscription.value?.status === 'past_due'
  )

  const isSubscriptionCanceled = computed(() =>
    subscription.value?.cancelAtPeriodEnd === true
  )

  const isSubscriptionPaused = computed(() =>
    subscription.value?.status === 'paused'
  )

  const isInGracePeriod = computed(() =>
    subscription.value?.isInGracePeriod === true
  )

  const needsUserSelection = computed(() =>
    subscription.value?.pendingUserSelection === true
  )

  const activeUserCount = computed(() =>
    subscription.value?.activeUserCount ?? 0
  )

  const billableUserCount = computed(() =>
    subscription.value?.billedUsers ?? 0
  )

  const freeUserLimit = computed(() =>
    subscription.value?.freeUserLimit ?? 5
  )

  const monthlyTotal = computed(() =>
    subscription.value?.monthlyTotal ?? 0
  )

  const canAddMoreUsers = computed(() => {
    if (!subscription.value) return true
    if (subscription.value.status === 'free') {
      return subscription.value.activeUserCount < subscription.value.freeUserLimit
    }
    // Active subscription — quantity auto-syncs, so users can always be added
    return true
  })

  const requiresSubscription = computed(() => {
    if (!subscription.value) return false
    return subscription.value.status === 'free' &&
      subscription.value.activeUserCount >= subscription.value.freeUserLimit
  })

  const statusColor = computed(() => {
    if (!subscription.value) return 'gray'

    switch (subscription.value.status) {
      case 'active':
        return 'green'
      case 'past_due':
        return 'yellow'
      case 'canceled':
        return 'red'
      case 'paused':
        return 'orange'
      case 'free':
        return 'blue'
      default:
        return 'gray'
    }
  })

  const statusLabel = computed(() => {
    if (!subscription.value) return 'Unknown'

    switch (subscription.value.status) {
      case 'active':
        return 'Active'
      case 'past_due':
        return 'Past Due'
      case 'canceled':
        return 'Canceled'
      case 'paused':
        return 'Paused'
      case 'free':
        return 'Free Plan'
      default:
        return subscription.value.status
    }
  })

  // Actions
  async function fetchSubscription(): Promise<void> {
    loading.value = true
    error.value = null

    try {
      const response = await subscriptionsApi.getCurrent()
      subscription.value = response.data as SubscriptionDetails
    } catch (err: any) {
      console.error('Error fetching subscription:', err)
      error.value = err.response?.data?.error?.message || err.message || 'Failed to fetch subscription'
    } finally {
      loading.value = false
    }
  }

  async function prepareCheckout(withAiAddon = false): Promise<PrepareCheckoutResponse | null> {
    loading.value = true
    error.value = null

    try {
      const response = await subscriptionsApi.prepareCheckout(withAiAddon)
      return response.data as PrepareCheckoutResponse
    } catch (err: any) {
      console.error('Error preparing checkout:', err)
      error.value = err.response?.data?.error?.message || err.message || 'Failed to prepare checkout'
      notifications.error('Failed to prepare checkout. Please try again.')
      return null
    } finally {
      loading.value = false
    }
  }

  async function cancelSubscription(): Promise<boolean> {
    loading.value = true
    error.value = null

    try {
      const response = await subscriptionsApi.cancel()
      subscription.value = response.data as SubscriptionDetails
      notifications.success('Subscription will be canceled at the end of the billing period')
      return true
    } catch (err: any) {
      console.error('Error canceling subscription:', err)
      error.value = err.response?.data?.error?.message || err.message || 'Failed to cancel subscription'
      notifications.error(error.value!)
      return false
    } finally {
      loading.value = false
    }
  }

  async function resumeSubscription(): Promise<boolean> {
    loading.value = true
    error.value = null

    try {
      const response = await subscriptionsApi.resume()
      subscription.value = response.data as SubscriptionDetails
      notifications.success('Subscription resumed successfully')
      return true
    } catch (err: any) {
      console.error('Error resuming subscription:', err)
      error.value = err.response?.data?.error?.message || err.message || 'Failed to resume subscription'
      notifications.error(error.value!)
      return false
    } finally {
      loading.value = false
    }
  }

  async function getPaymentMethodTransaction(): Promise<string | null> {
    loading.value = true
    error.value = null

    try {
      const response = await subscriptionsApi.getPaymentMethodTransaction()
      return (response.data as any).transactionId
    } catch (err: any) {
      console.error('Error getting payment method transaction:', err)
      error.value = err.response?.data?.error?.message || err.message || 'Failed to get payment update link'
      notifications.error('Failed to open payment method update. Please try again.')
      return null
    } finally {
      loading.value = false
    }
  }

  async function selectActiveUsers(userIds: string[]): Promise<boolean> {
    loading.value = true
    error.value = null

    try {
      await subscriptionsApi.selectActiveUsers(userIds)
      await fetchSubscription()
      notifications.success('Active users updated successfully')
      return true
    } catch (err: any) {
      console.error('Error selecting active users:', err)
      error.value = err.response?.data?.error?.message || err.message || 'Failed to select active users'
      notifications.error(error.value!)
      return false
    } finally {
      loading.value = false
    }
  }

  async function checkCanAddUser(): Promise<CanAddUserResponse | null> {
    try {
      const response = await subscriptionsApi.canAddUser()
      return response.data as CanAddUserResponse
    } catch (err: any) {
      console.error('Error checking user limit:', err)
      return null
    }
  }

  function clearSubscription(): void {
    subscription.value = null
    error.value = null
  }

  // ─── AI Call Analysis add-on ───
  const errorMessage = (err: any, fallback: string) =>
    err?.response?.data?.error?.message || err?.response?.data?.message || err?.message || fallback

  async function fetchAiUsage(): Promise<void> {
    try {
      const response = await subscriptionsApi.getAiUsage()
      aiUsage.value = response.data as AiUsage
    } catch {
      aiUsage.value = null
    }
  }

  async function previewAiAddon(enable: boolean): Promise<AiAddonPreview | null> {
    try {
      const response = await subscriptionsApi.previewAiAddon(enable)
      return response.data as AiAddonPreview
    } catch (err: any) {
      notifications.error(errorMessage(err, 'Could not price the change. Please try again.'))
      return null
    }
  }

  async function setAiAddon(enable: boolean): Promise<boolean> {
    try {
      const response = await subscriptionsApi.setAiAddon(enable)
      subscription.value = response.data as SubscriptionDetails
      await fetchAiUsage()
      notifications.success(enable ? 'AI Call Analysis added.' : 'AI Call Analysis removed.')
      return true
    } catch (err: any) {
      notifications.error(errorMessage(err, 'Could not change the add-on. Please try again.'))
      return false
    }
  }

  async function setAiOverageCap(blocks: number): Promise<boolean> {
    try {
      const response = await subscriptionsApi.setAiOverageCap(blocks)
      subscription.value = response.data as SubscriptionDetails
      await fetchAiUsage()
      notifications.success('Extra-call limit saved.')
      return true
    } catch (err: any) {
      notifications.error(errorMessage(err, 'Could not save the limit.'))
      return false
    }
  }

  return {
    aiUsage,
    fetchAiUsage,
    previewAiAddon,
    setAiAddon,
    setAiOverageCap,
    // State
    subscription,
    loading,
    error,

    // Computed
    isSubscriptionActive,
    isSubscriptionFree,
    isSubscriptionPastDue,
    isSubscriptionCanceled,
    isSubscriptionPaused,
    isInGracePeriod,
    needsUserSelection,
    activeUserCount,
    billableUserCount,
    freeUserLimit,
    monthlyTotal,
    canAddMoreUsers,
    requiresSubscription,
    statusColor,
    statusLabel,

    // Actions
    fetchSubscription,
    prepareCheckout,
    cancelSubscription,
    resumeSubscription,
    getPaymentMethodTransaction,
    selectActiveUsers,
    checkCanAddUser,
    clearSubscription,
  }
})
